import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../../../environments/environment';
import { Observable } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { CommonService } from '../../common-service';
import { ReturnStruct } from '../../structs/return.struct';
import { AdminSalesTimeSeriesRequest, SalesListRequest } from '../../requests/platform/sales-list.request';
import {
  AdminSaleDetailsResponse,
  AdminSalesExportResponse,
  AdminSalesIndicatorsResponse,
  AdminSalesListResponse,
  AdminSalesTimeSeriesResponse,
  AdminSalesTrackingResponse,
  AdminSalesUploadNfeResponse,
} from '../../responses/platform/admin-sales.response';

/** Dados que o admin informa ao enviar uma etiqueta gerada fora do fluxo automático. */
export interface UploadLabelData {
  trackingCode: string;
  serviceCode: string;
  prePostagemId?: string | null;
  replace?: boolean;
}

@Injectable({ providedIn: 'root' })
export class SalesService extends CommonService {
  private readonly base = environment.urlApiPlatform + 'AdminSales/';

  constructor(private http: HttpClient) { super(); }

  // -------- LISTA / DETALHE --------
  postList(req: SalesListRequest): Observable<AdminSalesListResponse> {
    return this.http
      .post<AdminSalesListResponse>(this.base + 'List', req, this.addHeaderToken())
      .pipe(catchError(this.handleError));
  }

  details(idKitPayment: number): Observable<AdminSaleDetailsResponse> {
    return this.http
      .get<AdminSaleDetailsResponse>(`${this.base}Details/${idKitPayment}`, this.addHeaderToken())
      .pipe(catchError(this.handleError));
  }

  tracking(idKitPayment: number, refresh = false): Observable<AdminSalesTrackingResponse> {
    return this.http
      .get<AdminSalesTrackingResponse>(`${this.base}Tracking/${idKitPayment}${refresh ? '?refresh=true' : ''}`, this.addHeaderToken())
      .pipe(catchError(this.handleError));
  }

  // -------- INDICADORES / GRÁFICO / EXPORTAÇÃO --------
  indicators(): Observable<AdminSalesIndicatorsResponse> {
    return this.http
      .get<AdminSalesIndicatorsResponse>(this.base + 'Indicators', this.addHeaderToken())
      .pipe(catchError(this.handleError));
  }

  timeSeries(req: AdminSalesTimeSeriesRequest): Observable<AdminSalesTimeSeriesResponse> {
    return this.http
      .post<AdminSalesTimeSeriesResponse>(this.base + 'TimeSeries', req, this.addHeaderToken())
      .pipe(catchError(this.handleError));
  }

  export(req: SalesListRequest): Observable<AdminSalesExportResponse> {
    return this.http
      .post<AdminSalesExportResponse>(this.base + 'Export', req, this.addHeaderToken())
      .pipe(catchError(this.handleError));
  }

  // -------- UPLOADS --------
  /** XML e DANFE no mesmo envio (qualquer um dos dois pode ficar vazio). */
  uploadNfe(idKitPayment: number, xml: File | null, danfe: File | null): Observable<AdminSalesUploadNfeResponse> {
    const form = new FormData();
    if (xml) form.append('xml', xml, xml.name);
    if (danfe) form.append('danfe', danfe, danfe.name);
    return this.http
      .post<AdminSalesUploadNfeResponse>(`${this.base}${idKitPayment}/UploadNfe`, form, this.fileOptions())
      .pipe(catchError(this.handleError));
  }

  uploadDanfePdf(idKitPayment: number, file: File): Observable<ReturnStruct> {
    const form = new FormData();
    form.append('file', file, file.name);
    return this.http
      .post<ReturnStruct>(`${this.base}${idKitPayment}/UploadDanfe`, form, this.fileOptions())
      .pipe(catchError(this.handleError));
  }

  uploadXmlNfe(idKitPayment: number, file: File): Observable<ReturnStruct> {
    const form = new FormData();
    form.append('file', file, file.name);
    return this.http
      .post<ReturnStruct>(`${this.base}${idKitPayment}/UploadXml`, form, this.fileOptions())
      .pipe(catchError(this.handleError));
  }

  /** Etiqueta manual: PDF + código de rastreio + serviço (+ pré-postagem opcional). */
  uploadLabel(idKitPayment: number, file: File, data: UploadLabelData): Observable<ReturnStruct> {
    const form = new FormData();
    form.append('file', file, file.name);
    form.append('trackingCode', data.trackingCode);
    form.append('serviceCode', data.serviceCode);
    if (data.prePostagemId) form.append('prePostagemId', data.prePostagemId);
    form.append('replace', data.replace ? 'true' : 'false');
    return this.http
      .post<ReturnStruct>(`${this.base}${idKitPayment}/UploadLabel`, form, this.fileOptions())
      .pipe(catchError(this.handleError));
  }

  setNfeMeta(idKitPayment: number, nfeNumber: string, nfeKey: string): Observable<ReturnStruct> {
    return this.http
      .post<ReturnStruct>(`${this.base}${idKitPayment}/SetNfeMeta`, { nfeNumber, nfeKey }, this.addHeaderToken())
      .pipe(catchError(this.handleError));
  }

  /** Headers de autenticação sem Content-Type (o browser define o boundary do multipart). */
  private fileOptions() {
    const options = this.addHeaderToken();
    if (options?.headers?.has('Content-Type')) {
      options.headers = options.headers.delete('Content-Type');
    }
    return options;
  }
}
